import React from 'react';
import { Modal } from './Modal';
import { CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';

interface SyncProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel?: () => void;
  total: number;
  current: number;
  currentProduct?: {
    clientNumber: string;
    items: string[];
    totalPrice: number;
  };
  status: 'syncing' | 'completed' | 'error' | 'cancelled';
  error?: string;
}

export function SyncProgressModal({
  isOpen,
  onClose,
  onCancel,
  total,
  current,
  currentProduct,
  status,
  error
}: SyncProgressModalProps) {
  const progress = Math.round((current / total) * 100);

  const renderStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'error':
        return <AlertTriangle className="w-16 h-16 text-red-500" />;
      case 'cancelled':
        return <XCircle className="w-16 h-16 text-yellow-500" />;
      default:
        return <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />;
    }
  };

  const renderStatusText = () => {
    switch (status) {
      case 'completed':
        return 'Synchronisation terminée avec succès';
      case 'error':
        return 'Une erreur est survenue';
      case 'cancelled':
        return 'Synchronisation annulée';
      default:
        return 'Synchronisation en cours...';
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={status !== 'syncing' ? onClose : undefined}
      title="Progression de la synchronisation"
    >
      <div className="space-y-6">
        <div className="text-center">
          {renderStatusIcon()}
          <h3 className="text-xl font-semibold mt-4">
            {renderStatusText()}
          </h3>
        </div>

        <div className="space-y-4">
          {/* Barre de progression */}
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block text-blue-600">
                  Progression
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-blue-600">
                  {progress}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 text-xs flex rounded bg-blue-100">
              <div
                style={{ width: `${progress}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-500 ease-out"
              />
            </div>
          </div>

          {/* Compteurs */}
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-gray-700">{current}</div>
              <div className="text-sm text-gray-500">Produits synchronisés</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-gray-700">{total}</div>
              <div className="text-sm text-gray-500">Total à synchroniser</div>
            </div>
          </div>

          {/* Produit en cours */}
          {status === 'syncing' && currentProduct && (
            <div className="bg-white border rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Produit en cours de synchronisation
              </h4>
              <div className="space-y-1">
                <p className="text-sm">
                  <span className="font-medium">Client:</span> #{currentProduct.clientNumber}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Articles:</span>{' '}
                  {currentProduct.items.join(', ')}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Prix:</span> {currentProduct.totalPrice}€
                </p>
              </div>
            </div>
          )}

          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          {status === 'syncing' && onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
            >
              Annuler
            </button>
          )}
          {status !== 'syncing' && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Fermer
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}